import React from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  RocketLaunch as LaunchIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  CheckCircle as VerifiedIcon,
  ShieldOutlined as ShieldIcon,
  Bolt as InstantIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { DEFAULT_CURRENCY, LOTTERY_NAMES, LOTTERY_TYPES } from "../../../../../static/Constants";
import { CommonButton, CommonChip } from "../../../../Components/ui";

const MissileSummaryCard = ({
  lotteryId,
  entryFeeFormatted = "0",
  totalPrizeFormatted = "0",
  currency = DEFAULT_CURRENCY,
  pickNumbers = 3,
  selectedNumber = "",
  tierInfo,
  account = "",
  loading = false,
  onBuy,
  onConnectWallet,
}) => {
  const typeConfig = LOTTERY_TYPES[3];
  const maxDigits = Math.max(1, Number(pickNumbers) || 3);
  const isCodeComplete = selectedNumber.length === maxDigits;
  const isWalletConnected = Boolean(account);

  const truncatedAccount = account
    ? `${account.slice(0, 6)}...${account.slice(-4)}`
    : "";

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.slate[200]}`,
        bgcolor: "#ffffff",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.06)",
        position: { lg: "sticky" },
        top: { lg: 100 },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        {/* Card Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: colors.slate[900],
            mb: 0.5,
          }}
        >
          Launch Order Summary
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: colors.slate[500], display: "block", mb: 2.5 }}
        >
          Review your missile launch parameters and execute on-chain.
        </Typography>

        {/* Selected Target Code Box */}
        <Box
          sx={{
            bgcolor: colors.slate[50],
            borderRadius: borderRadiusPx.lg,
            p: 2,
            mb: 2.5,
            border: `1px solid ${colors.slate[200]}`,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="caption" sx={{ color: colors.slate[500], fontWeight: 600 }}>
              Chosen Target Code
            </Typography>
            {isCodeComplete ? (
              <CommonChip
                label="Complete"
                variant="success"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            ) : (
              <CommonChip
                label={`${selectedNumber.length}/${maxDigits} digits`}
                variant="warning"
                size="small"
              />
            )}
          </Stack>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontFamily: "monospace",
              color: isCodeComplete ? typeConfig.color : colors.slate[400],
              letterSpacing: maxDigits > 5 ? "0.06em" : "0.15em",
              fontSize:
                maxDigits > 6
                  ? { xs: "1.15rem", sm: "1.35rem" }
                  : maxDigits > 4
                  ? { xs: "1.4rem", sm: "1.65rem" }
                  : { xs: "1.75rem", sm: "2.125rem" },
              wordBreak: "break-all",
              lineHeight: 1.3,
            }}
          >
            {selectedNumber
              ? selectedNumber.padEnd(maxDigits, " _ ")
              : "— — —"}
          </Typography>
        </Box>

        {/* Line Items Breakdown */}
        <Stack spacing={1.5} sx={{ mb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ color: colors.slate[600] }}>
              Lottery Ref
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.slate[900] }}>
              {`${LOTTERY_NAMES.ROCKETBET} #${lotteryId}`}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ color: colors.slate[600] }}>
              Missile Tier
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.slate[900] }}>
              {tierInfo?.name || "Rocket Launch"}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ color: colors.slate[600] }}>
              Est. Prize Pool
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: typeConfig.color }}>
              {totalPrizeFormatted} {currency}
            </Typography>
          </Stack>

          <Divider sx={{ my: 1, borderColor: colors.slate[200] }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.slate[900] }}>
              Launch Price
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: typeConfig.color }}>
              {entryFeeFormatted} {currency}
            </Typography>
          </Stack>
        </Stack>

        {/* Wallet Status / Connector */}
        <Box sx={{ mb: 2.5 }}>
          {isWalletConnected ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 1.25,
                bgcolor: colors.slate[50],
                borderRadius: borderRadiusPx.lg,
                border: `1px solid ${colors.slate[200]}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <WalletIcon sx={{ fontSize: 18, color: typeConfig.color }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: colors.slate[700] }}>
                  {truncatedAccount}
                </Typography>
              </Stack>
              <VerifiedIcon sx={{ fontSize: 16, color: colors.emerald[500] }} />
            </Stack>
          ) : (
            <CommonButton
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<WalletIcon fontSize="small" />}
              onClick={onConnectWallet}
              label="Connect Wallet"
              sx={{
                py: 1,
                color: typeConfig.color,
                borderColor: typeConfig.borderColor,
                "&:hover": {
                  bgcolor: typeConfig.bgColor,
                  borderColor: typeConfig.color,
                  color: typeConfig.color,
                },
              }}
            />
          )}
        </Box>

        {/* Main Action Button */}
        {!isWalletConnected ? (
          <CommonButton
            variant="play"
            size="large"
            fullWidth
            onClick={onConnectWallet}
            startIcon={<WalletIcon />}
            label="Connect Wallet to Launch"
            sx={{
              py: 1.5,
              fontWeight: 800,
              fontSize: "1rem",
              background: typeConfig.gradient,
              boxShadow: typeConfig.boxShadow,
              "&:hover": {
                background: typeConfig.gradient,
                boxShadow: `0 6px 20px ${typeConfig.glow}`,
                transform: "translateY(-1px)",
                filter: "brightness(1.08)",
              },
            }}
          />
        ) : (
          <CommonButton
            variant="play"
            size="large"
            fullWidth
            loading={loading}
            disabled={!isCodeComplete || loading}
            onClick={onBuy}
            startIcon={!loading ? <LaunchIcon /> : null}
            label={
              loading
                ? "Launching On-Chain..."
                : !isCodeComplete
                ? `Enter ${maxDigits} Digits to Launch`
                : `Launch for ${entryFeeFormatted} ${currency}`
            }
            sx={{
              py: 1.5,
              fontWeight: 800,
              fontSize: "1rem",
              background: isCodeComplete && !loading ? typeConfig.gradient : undefined,
              boxShadow: isCodeComplete && !loading ? typeConfig.boxShadow : undefined,
              "&:hover": {
                background: isCodeComplete && !loading ? typeConfig.gradient : undefined,
                boxShadow: isCodeComplete && !loading ? `0 6px 20px ${typeConfig.glow}` : undefined,
                transform: isCodeComplete && !loading ? "translateY(-1px)" : undefined,
                filter: isCodeComplete && !loading ? "brightness(1.08)" : undefined,
              },
              "&.Mui-disabled": {
                background: colors.slate[200],
                color: colors.slate[400],
                boxShadow: "none",
              },
            }}
          />
        )}

        {/* Trust Badges */}
        <Stack spacing={1} sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.slate[100]}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShieldIcon sx={{ fontSize: 16, color: colors.emerald[500] }} />
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              Smart Contract Verifiable Non-Custodial Launch
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InstantIcon sx={{ fontSize: 16, color: colors.amber[500] }} />
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              Instant Automated Settlement on Draw
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MissileSummaryCard;
